<template>
  <div class="document-path" :title="path || name">
    <button
      v-if="isFile"
      @click="handleClickOpen"
    >
      <ArrowDownTrayIcon />
      {{ name }}
    </button>
    <a
      v-else
      :href="path"
      target="_blank"
      rel="noopener noreferrer"
    >
      <LinkIcon />
      {{ path }}
    </a>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { DocumentClass } from '@types'
import { DOCUMENT_FIELD } from '@/entities/documents/utils/const'
import { getDocumentUrl } from '@/components/UI/Document/utils/helpers'
import { handleExternalLink } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'

import { ArrowDownTrayIcon, LinkIcon } from '@heroicons/vue/24/outline'

type Props = {
  item: DocumentClass
}

const { item } = defineProps<Props>()

const { error } = useNotifications()

const loading = ref(false)
const internalUrl = ref<string>()

const isFile = computed(() => item.isFile)
const path = computed(() => item.field<string>(DOCUMENT_FIELD.PATH).value)
const name = computed(() => item.field<string>(DOCUMENT_FIELD.NAME).value)

const handleClickOpen = async () => {
  if (internalUrl.value) {
    handleExternalLink(internalUrl.value)
    return
  }

  loading.value = true
  try {
    const url = await getDocumentUrl(item.id)
    internalUrl.value = url
    handleExternalLink(url)
  } catch (e) {
    error({
      message: 'Failed to open document',
      description: (e as any).message,
    })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.document-path {

  button, a {
    @apply flex items-center gap-1;
    @apply max-w-40;
    @apply text-sm sm:text-xs text-primary;

    svg {
      @apply w-4 h-4 shrink-0;
    }
  }
}
</style>
